import React from 'react';
import { formatClassificationMarking, partitionCaveatsIntoSlots } from '../utils/classificationMarking';

// Get stamp class based on classification level
function getStampClass(level) {
  const upperLevel = (level || '').toUpperCase();
  if (upperLevel.includes('TOP SECRET')) return 'stamp-topsecret';
  if (upperLevel.includes('SECRET')) return 'stamp-secret';
  if (upperLevel.includes('CONFIDENTIAL')) return 'stamp-confidential';
  if (upperLevel.includes('UNCLASSIFIED') || upperLevel.includes('PUBLIC')) return 'stamp-unclassified';
  return 'stamp-unclassified';
}

// Function to clean caveats and releasability arrays
function cleanClassificationComponents(caveats = [], releasability = []) {
  // Valid caveats (short codes only)
  const validCaveats = ['SI', 'HCS', 'TK', 'GAMMA', 'ORCON', 'FOUO', 'LIMDIS'];

  // Clean caveats - only keep recognized short codes, filter out verbose explanations
  const cleanCaveats = caveats.filter(caveat => {
    if (!caveat || typeof caveat !== 'string') return false;
    const upperCaveat = caveat.toUpperCase().trim();
    return validCaveats.includes(upperCaveat);
  });

  // Clean releasability - only keep entries that look like proper releasability markings
  const cleanReleasability = releasability.filter(rel => {
    if (!rel || typeof rel !== 'string') return false;
    const upperRel = rel.toUpperCase().trim();
    return upperRel === 'NOFORN' ||
           upperRel.startsWith('REL TO') ||
           upperRel.startsWith('RELTO') ||
           upperRel === 'NF';
  });

  return { cleanCaveats, cleanReleasability };
}

// Build the canonical IC marking from the result components.
// Per IC standards: // separates categories, / separates items within a category.
// Backend `caveats` field can mix dissemination + compartments — partitioner sorts them.
export function extractCleanClassification(fullClassification, baseClassification, caveats = [], releasability = []) {
  const { cleanCaveats, cleanReleasability } = cleanClassificationComponents(caveats, releasability);

  if (cleanCaveats.length > 0 || cleanReleasability.length > 0) {
    const slots = partitionCaveatsIntoSlots(cleanCaveats, [], cleanReleasability);
    return formatClassificationMarking({ level: baseClassification, ...slots });
  }

  if (!fullClassification || fullClassification === baseClassification) {
    const slots = partitionCaveatsIntoSlots(caveats, [], releasability);
    return formatClassificationMarking({ level: baseClassification, ...slots });
  }

  // Last-resort parse of a garbled backend string.
  let cleaned = fullClassification.toUpperCase();
  const classificationLevels = ['TOP SECRET', 'SECRET', 'CONFIDENTIAL', 'UNCLASSIFIED'];
  let baseLevel = 'UNKNOWN';
  for (const level of classificationLevels) {
    if (cleaned.includes(level)) { baseLevel = level; break; }
  }
  const knownCompartments = ['SI', 'HCS', 'TK', 'GAMMA'];
  const knownDissem = ['NOFORN', 'ORCON', 'FOUO', 'LIMDIS'];
  const foundCompartments = knownCompartments.filter(c =>
    cleaned.includes('//' + c + '//') || cleaned.includes(' ' + c + ' ')
    || cleaned.includes('//' + c) || cleaned.includes('/' + c + '/')
    || cleaned.includes('/' + c + '//') || cleaned.endsWith('/' + c)
  );
  const foundDissem = knownDissem.filter(d => cleaned.includes(d));
  return formatClassificationMarking({
    level: baseLevel,
    compartments: foundCompartments,
    dissemination: foundDissem,
  });
}

export default function ClassificationBadge({
  classification,
  fullClassification,
  caveats = [],
  releasability = [],
  confidence,
  className = '',
  size = 'default' // 'small', 'default', 'large'
}) {
  // Clean and extract proper classification
  const displayText = extractCleanClassification(fullClassification, classification, caveats, releasability);

  // Get base level for styling (first part before //)
  const baseLevel = displayText.split('//')[0];
  const stampClass = getStampClass(baseLevel);

  // Size variants
  const sizeClasses = {
    small: 'text-[9px] py-0.5 px-2',
    default: '',
    large: 'text-xs py-1.5 px-4'
  };

  return (
    <span className={`stamp ${stampClass} ${sizeClasses[size]} ${className}`}>
      <span>{displayText}</span>
      {confidence && (
        <span className="ml-2 opacity-70">
          {Math.round(confidence * 100)}%
        </span>
      )}
    </span>
  );
}
